/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  // ============================================================
  // ProductCardSlideup — Daniel 2026-04-03, updated 2026-04-06
  //
  // Custom element powering the two-step inline slideup panel on
  // product grid cards.
  //
  // Desktop flow:
  //   1. Card hover → CSS slides up .product-card-slideup-wrapper--desktop,
  //      showing the trigger button.
  //   2. Trigger click → JS adds .is-open to wrapper → trigger hides,
  //      full panel (variant pickers + price + ATC) appears.
  //   3. Card mouseleave → JS removes .is-open, wrapper slides back down.
  //
  // Mobile flow:
  //   1. Trigger button is always visible between image and title.
  //   2. Tap trigger → JS adds .is-open to the panel element → panel slides
  //      up as overlay over the product image.
  //   3. Tap outside trigger + panel → JS removes .is-open.
  //
  // Also handles:
  //   - Variant selection with live price updates
  //   - ATC via direct POST to /cart/add.js
  //   - Cart refresh via theme:cart:refresh CustomEvent
  // ============================================================

  class ProductCardSlideup extends HTMLElement {
    connectedCallback() {
      // Guard: connectedCallback fires again if a carousel re-inserts this
      // element. Without this, listeners stack up and cause open-then-close.
      if (this._initialized) return;
      this._initialized = true;

      this._variants      = JSON.parse(this.dataset.variants || '[]');
      this._optionSelects = Array.from(this.querySelectorAll('[data-option-select]'));
      this._atcBtn        = this.querySelector('[data-slideup-atc]');
      this._priceEl       = this.querySelector('[data-slideup-price]');
      this._errorEl       = this.querySelector('[data-slideup-error]');

      this._optionSelects.forEach(sel => {
        sel.addEventListener('change', () => this._onOptionChange());
      });

      if (this._atcBtn) {
        this._atcBtn.addEventListener('click', () => this._addToCart());
      }

      // Stop all clicks that originate inside this panel from bubbling up to
      // Broadcast's theme handlers (quickview, slider, section JS). Without
      // this, a click on a select or ATC button reaches the grid-item and
      // triggers Broadcast's quickview/quick-add logic, causing all cards to
      // briefly show their buttons and breaking the slideup afterward.
      this.addEventListener('click', e => e.stopPropagation());

      // grid-slider's draggableSlider calls preventDefault() on every mousedown
      // on the slider element (bubble phase), which blocks native <select> focus
      // and button activation when the product card is inside a slider layout.
      // Stop propagation here so those events never reach the slider handler.
      const _stopDrag = e => e.stopPropagation();
      this._optionSelects.forEach(sel => {
        sel.addEventListener('mousedown', _stopDrag);
      });
      if (this._atcBtn) {
        this._atcBtn.addEventListener('mousedown', _stopDrag);
      }

      // ── Two-step trigger wiring ───────────────────────────────

      this._wrapper = this.closest('[data-slideup-wrapper]');

      if (this._wrapper) {
        // Desktop: trigger lives inside the wrapper alongside this panel.
        this._triggerBtn = this._wrapper.querySelector('[data-slideup-trigger]');

        // Attach hover to the image container only — the button should not
        // appear when hovering the product title/price/stars section below.
        this._card = this.closest('[data-product-image]');
        if (this._card) {
          this._onCardEnter = () => this._wrapper.classList.add('is-hovered');
          this._onCardLeave = () => {
            this._closePanel();
            this._wrapper.classList.remove('is-hovered');
          };
          this._card.addEventListener('mouseenter', this._onCardEnter);
          this._card.addEventListener('mouseleave', this._onCardLeave);
        }
      } else {
        // Mobile: trigger references this panel via data-slideup-for="<id>".
        this._triggerBtn = this.id
          ? document.querySelector('[data-slideup-for="' + this.id + '"]')
          : null;

        // Handler to close panel when user taps outside trigger + panel.
        this._onDocClick = (e) => {
          const outside = !this.contains(e.target) &&
            e.target !== this._triggerBtn &&
            !(this._triggerBtn && this._triggerBtn.contains(e.target));
          if (outside) this._closePanel();
        };
      }

      if (this._triggerBtn) {
        this._triggerBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this._variants.length <= 1) {
            this._addToCart(this._triggerBtn);
          } else {
            this._isOpen() ? this._closePanel() : this._openPanel();
          }
        });
      }
    }

    disconnectedCallback() {
      // Allow re-initialization if element is reconnected (e.g., carousel loop).
      this._initialized = false;
      if (this._card) {
        if (this._onCardEnter) this._card.removeEventListener('mouseenter', this._onCardEnter);
        if (this._onCardLeave) this._card.removeEventListener('mouseleave', this._onCardLeave);
      }
      if (this._onDocClick) {
        document.removeEventListener('click', this._onDocClick, { capture: true });
      }
    }

    // ── Panel open / close ─────────────────────────────────────

    _isOpen() {
      return this._wrapper
        ? this._wrapper.classList.contains('is-open')
        : this.classList.contains('is-open');
    }

    _openPanel() {
      if (this._wrapper) {
        // Desktop: add is-open + is-hovered to wrapper.
        // is-hovered is normally set by mouseenter, but on touch or keyboard
        // the enter event may not have fired, so we add it here as a safety net.
        this._wrapper.classList.add('is-open', 'is-hovered');
        if (this._triggerBtn) this._triggerBtn.setAttribute('aria-expanded', 'true');
      } else {
        // Mobile: add is-open to this element (CSS slides panel up).
        this.classList.add('is-open');
        if (this._triggerBtn) this._triggerBtn.setAttribute('aria-expanded', 'true');
        // Defer listener so this same click doesn't immediately close the panel.
        setTimeout(() => {
          document.addEventListener('click', this._onDocClick, { capture: true });
        }, 0);
      }
    }

    _closePanel() {
      if (this._wrapper) {
        // Only remove is-open here. is-hovered is intentionally left in place
        // so the trigger stays visible if the mouse is still on the card.
        // _onCardLeave removes is-hovered when the mouse actually exits.
        this._wrapper.classList.remove('is-open');
        if (this._triggerBtn) this._triggerBtn.setAttribute('aria-expanded', 'false');
      } else {
        this.classList.remove('is-open');
        if (this._triggerBtn) this._triggerBtn.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this._onDocClick, { capture: true });
      }
    }

    // ── Variant selection via <select> dropdowns ───────────────

    _onOptionChange() {
      const variant = this._findVariant();
      if (!variant) return;

      if (this._atcBtn) {
        this._atcBtn.dataset.variantId = variant.id;
        this._atcBtn.disabled = !variant.available;
      }

      this._updatePrice(variant.price, variant.compare_at_price || 0);
    }

    _findVariant() {
      const selected = this._optionSelects.map(s => s.value);
      return this._variants.find(v => {
        // Shopify serializes option1/option2/option3 as flat strings; the
        // options array may or may not be present depending on API version.
        const opts = Array.isArray(v.options)
          ? v.options
          : [v.option1, v.option2, v.option3].filter(opt => opt != null);
        return opts.length === selected.length &&
               opts.every((opt, i) => opt === selected[i]);
      });
    }

    // ── Price formatting ───────────────────────────────────────

    _formatMoney(cents) {
      // Prefer Shopify.formatMoney (respects shop money format).
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    _updatePrice(price, compare) {
      if (!this._priceEl) return;

      var html = '<span class="price__current">' + this._formatMoney(price) + '</span>';
      if (compare > price) {
        html += ' <s class="price__was">' + this._formatMoney(compare) + '</s>';
      }
      this._priceEl.innerHTML = html;
    }

    // ── Add to cart ────────────────────────────────────────────

    async _addToCart(uiBtn) {
      // POST directly to /cart/add.js — bypasses Broadcast's theme:cart:add
      // event flow which expects event.detail.button with an attached form.
      // uiBtn is the button that receives loading/added state; variant ID always
      // comes from this._atcBtn so single-variant trigger clicks work correctly.
      const variantId = this._atcBtn && this._atcBtn.dataset.variantId;
      if (!variantId) return;

      const btn = uiBtn || this._atcBtn;
      btn.disabled = true;
      btn.classList.add('is-loading');
      if (this._errorEl) this._errorEl.textContent = '\u00a0';

      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart');
        }

        btn.classList.remove('is-loading');
        btn.classList.add('is-added');

        // theme:cart:refresh → Broadcast's CartDrawer calls getCart() which fetches
        // the cart section, then dispatches theme:cart:change (updates count badges)
        // and theme:product:added (triggers openCartDrawerOnProductAdded).
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        // theme:cart:refresh → Broadcast's CartDrawer calls getCart() which fetches
        // the cart section, then dispatches theme:cart:change (updates count badges)
        // and theme:product:added (triggers openCartDrawerOnProductAdded).
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

        // Open Upsell Cart drawer. The slideup POSTs to /cart/add.js directly,
        // bypassing AMP's form[action$="/cart/add"] click detection, so the app
        // never opens on its own here. Call its public API explicitly.
        if (window.AMP_API && typeof window.AMP_API.OPEN_CART === 'function') {
          window.AMP_API.OPEN_CART();
        }

        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 2000);

      } catch (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;

        if (this._errorEl) {
          this._errorEl.textContent = err.message || 'Error';
          setTimeout(() => {
            if (this._errorEl) this._errorEl.textContent = '\u00a0';
          }, 3500);
        }
      }
    }
  }

  if (!customElements.get('product-card-slideup')) {
    customElements.define('product-card-slideup', ProductCardSlideup);
  }

  // Subscription-only cards render a plain <div class="product-card-slideup--desktop">
  // (not a custom element), so connectedCallback never runs for them.
  // Wire up their hover separately using the same is-hovered approach.
  function initSubscriptionSlideups() {
    document.querySelectorAll('[data-product-image]').forEach(function(img) {
      var el = img.querySelector('.product-card-slideup--desktop');
      if (!el) return;
      img.addEventListener('mouseenter', function() { el.classList.add('is-hovered'); });
      img.addEventListener('mouseleave', function() { el.classList.remove('is-hovered'); });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubscriptionSlideups);
  } else {
    initSubscriptionSlideups();
  }

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();